import { EXAMS } from '../data/mock/exams';
import { toBundle } from '../data/mock/toBundle';
import { importExam } from '../persistence/importExam';
import type { Store } from '../persistence/port';
import { loadSettings, saveSetting } from '../persistence/settings';
import { neuerWert } from '../platform/alteNamen';
import { KATALOG, ausRelay, leseEinstellungen } from '../platform/einstellungen';
import { leseQrUeberschreibungen } from '../persistence/qrUeberschreibungen';
import { setzeNamensform } from '../platform/storage';
import { APP_VERSION_MIT_BUILD } from '../platform/appVersion';
import { systemJetzt } from '../platform/betriebssystem';
import { istDesktop, istGeraet } from '../platform/gleis';
import { Signing, type ManagedSettings } from '../platform/signing';
import { createServerClient, type ServerClient } from './client';
import { holeClassicKatalog } from './classic/katalog';
import { createClassicClient } from './classic/client';
import { holeKatalog } from './katalog';
import type { ExamRef } from '../domain/examCatalogue';

/**
 * Wo der Server steht und womit die App sich dort anmeldet.
 *
 * **Adresse und Anmeldename** sind gewöhnliche Einstellungen (`ClientSetting`) — sie stehen in
 * der Datenbank und dürfen dort stehen. **Das Passwort nicht:** es liegt im Schlüsselbund
 * (`platform/signing.ts::setSecret`), im Browser nur im Speicher der Seite.
 *
 * ⚠️ **Ohne Adresse ist die App nicht kaputt, sondern ohne Server.** Das ist derzeit der
 * Normalfall — Relay kann den Inhalt einer Prüfung noch nicht liefern. Dafür gibt es die
 * Beispielprüfungen unten.
 */

export const SCHLUESSEL = {
  url: 'server.url',
  login: 'server.login',
  modus: 'server.modus',
} as const;

/**
 * Mit welchem Server gesprochen wird.
 *
 * `'classic'` ist der Kompatibilitätsmodus: dieselbe App, aber das Protokoll des alten
 * Classic-Servers (`src/server/classic/`, beschrieben in `docs/protokoll-classic.md`). Der ganze
 * Unterschied steckt hinter `baueClient` und `holeKatalogFuer` — darüber merkt niemand etwas.
 *
 * Zum Ausbauen: `src/server/classic/` löschen, die drei `modus`-Verzweigungen in dieser Datei
 * zurückbauen, die Zeile in `ServerGroup.tsx` und den `PSMultiValueSpecifier` in der
 * `Root.plist`.
 */
export type Servermodus = 'relay' | 'classic';

/**
 * Nur die beiden bekannten Werte gelten; alles andere fällt auf die Katalogvorgabe.
 *
 * ⚠️ **Die Vorgabe stand hier einmal im Code** (`sonst Relay`) und zugleich im Katalog
 * (`server_protocol.vorgabe`). Am 2026-09-18 liefen die beiden auseinander: der Katalog sagte
 * Classic, der Code Relay. Ein frisches iPad hat `server_protocol` leer — iOS legt die
 * `DefaultValue` der `Root.plist` erst an, wenn jemand die Seite einmal geöffnet hat —, also
 * gewann der Code. Die App sprach Relay mit einem Classic-Server, meldete sich per
 * `POST /rest/v1/login/login` **ohne** Basic-Kopfzeile an, und der Wächter des Webservers antwortete 401.
 * Seither ist der Katalog auch hier die eine Quelle (s. `platform/einstellungen.ts`).
 */
export function alsModus(wert: string | undefined | null): Servermodus {
  // Alte Werte (`RELAY`, `METIS`, `CLASSIC`) — s. `platform/alteNamen.ts::ALTE_WERTE`.
  const neu = neuerWert('server_protocol', wert);
  if (neu === 'classic' || neu === 'relay') return neu;
  return KATALOG.server_protocol.vorgabe;
}

const PASSWORT_SCHLUESSEL = 'server.password';

/**
 * Die Adresse, auf die ein **Entwicklungslauf im Browser** zeigt, solange nichts eingetragen ist.
 *
 * ⭐ **Die eigene Herkunft**, also `http://localhost:5173`. Dort liegt der Entwicklungsserver,
 * und der reicht `/rest/…` an Relay weiter (`vite.config.ts`, `server.proxy`). Damit läuft
 * `npm run dev` gegen den echten Server, ohne dass jemand erst ins Einstellungsblatt muss —
 * und ohne dass der Browser dem selbstsignierten Zertifikat von Relay vertrauen müsste: das
 * Zertifikat sieht der Entwicklungsserver, nicht der Browser.
 *
 * ⚠️ **Nur im Entwicklungsbau.** `import.meta.env.DEV` ist im gebauten Bündel falsch; ein
 * fertiges Bündel zeigt nie auf seine eigene Herkunft. Auf dem Gerät greift ohnehin der obere
 * Zweig von `ladeZugang`.
 *
 * ⚠️ **Eine gesicherte Adresse schlägt sie.** Wer im Einstellungsblatt etwas einträgt, will
 * dorthin — nicht auf den Entwicklungsserver.
 */
function entwicklungsAdresse(): string {
  if (!import.meta.env.DEV) return '';
  // Im Testlauf gibt es kein `window`; dort bleibt es beim alten, leeren Verhalten.
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

/**
 * Der Zugang, wenn im Browser nichts gesichert ist und der Entwicklungsbau läuft.
 *
 * ⚠️ **Der Anmeldename muss mit.** `istEingerichtet` verlangt Adresse **und** Name; mit
 * gesetzter Adresse und leerem Namen stünde die Prüfungsauswahl weiter auf „Kein Server
 * eingerichtet" — am 2026-09-22 genau so gesehen, bevor diese Zeile dazukam.
 *
 * ⚠️ **Im Relay-Modus wird er gar nicht benutzt**: dort meldet sich das feste Gerätekonto an
 * (`RELAY_ZUGANG` in `baueClient`). Er steht hier, damit die App sich als eingerichtet
 * versteht — und weil derselbe Rückfall auch dem Classic-Gleis am Schreibtisch dient.
 */
function entwicklungsZugang(): { url: string; login: string } {
  const url = entwicklungsAdresse();
  return url === '' ? { url: '', login: '' } : { url, login: VORGABE.login };
}

/**
 * Vorbelegung der Felder, solange nichts eingerichtet ist — der Entwicklungsserver des
 * Nutzers (Angabe vom 2026-09-11, Port 8445 am 2026-09-12 nachgetragen).
 *
 * ⚠️ **Seit dem 2026-09-22 zeigt sie auf Relay, `:8111`** (Vorgabe des Nutzers). Bis dahin
 * stand hier `:8445` — und dort lauscht ein **Classic**-Server (`WWW-Authenticate: basic`,
 * Basic-Auth-Endpunkt). Zusammen mit `server_protocol = CLASSIC` fuhr ein frisches Gerät damit
 * zwangsläufig auf dem Classic-Gleis, egal was für Relay gebaut war. `:8111` beantwortet
 * `POST /rest/v1/login/login` mit Rolle `DEVICE` und liefert `/exam/available` und
 * `/exam/document`; beides am 2026-09-22 gemessen.
 *
 * ⚠️ **Im Browser ist sie Vorbelegung, auf dem Gerät greift sie wirklich.** Im unteren Zweig
 * von `ladeZugang` bleibt die Adresse leer, bis der Prüfer gesichert hat — `istEingerichtet`
 * ist dort also falsch. Auf dem iPad läuft der **obere** Zweig (`verwaltet.available`), und
 * dort fällt jedes leere Feld bis hierher durch: die Adresse ist nie leer, `istEingerichtet`
 * damit **stets wahr**. Ein frisches iPad zeigt folglich auf diesen Server.
 *
 * Seit die Beispielprüfungen nur noch auf Knopfdruck kommen (`persistence/hydrate.ts`), hängt
 * daran kein zweiter Fehler mehr — die leere Prüfungsliste ist der ehrliche Anfangszustand,
 * unabhängig davon, ob ein Server eingetragen ist.
 *
 * ⚠️ **Das Passwort steht damit im Quelltext** und in jedem gebauten App-Bündel. Für einen
 * Entwicklungsserver im eigenen Netz ist das die Entscheidung des Nutzers; für den
 * Prüfungsbetrieb gehört hier eine leere Vorgabe hin, und das Passwort kommt vom Betreiber.
 */
export const VORGABE = {
  url: 'https://192.168.178.88:8111',
  login: 'admin',
  password: 'sgdrkp',
} as const;

/**
 * Das Gerätekonto von Relay (Festlegung des Nutzers, 2026-09-20).
 *
 * ⭐ **Zwei Server, zwei Konten.** Der Classic-Server kennt einen **Menschen** — `admin`, per
 * HTTP Basic, mehr sieht er nicht. Relay kennt ein **Gerät**: die Anmeldung legt eine
 * `Client`-Zeile an, und dafür gibt es ein eigenes Konto. Bis heute gab es ein Feldpaar für
 * beide; es konnte nur für einen von beiden stimmen, und genau das war der Grund, warum App-
 * und iPad-Vorgabe auseinanderliefen.
 *
 * ⚠️ **Es steht nicht in den Classic-Feldern.** Anmeldename und Passwort im Einstellungsdialog
 * bleiben der **Classic**-Zugang (`classic_login`/`classic_password`) — stünde das Gerätekonto
 * dort, schriebe ein Sichern im Relay-Modus es in den Classic-Zugang und machte ihn unbrauchbar.
 *
 * ⭐ **Seit 2026-10-01 eine Einstellung wie jede andere** (`communication_login`/
 * `communication_password`, Relays Namen; Entscheidung des Nutzers für zwei getrennte Paare).
 * Bis dahin stand es nur hier und liess sich allein durch QR oder MDM ersetzen, und auch das
 * nur über die Classic-Felder. Jetzt gilt die Rangfolge — QR, MDM, iPad-Seite, Relays
 * `SET_SETTINGS` — und dieser Wert ist nur noch die Vorgabe des Katalogs.
 *
 * ⚠️ **Damit steht auch dieses Passwort im Quelltext** und in jedem gebauten Bündel — wie das
 * Classic-Passwort seit dem 2026-09-11. Ausdrückliche Entscheidung des Nutzers; für einen
 * echten Prüfungsbetrieb gehört es vom Betreiber gesetzt.
 */
export const RELAY_ZUGANG = {
  login: KATALOG.communication_login.vorgabe,
  password: KATALOG.communication_password.vorgabe,
} as const;

export interface Zugangsdaten {
  url: string;
  login: string;
  password: string;
  modus: Servermodus;
  /**
   * Das Gerätekonto für Relay (`communication_login`/`communication_password`), nach der
   * Rangfolge gemischt. Fehlt es, gilt `RELAY_ZUGANG`.
   *
   * ⚠️ **Getrennt von `login`/`password`.** Die beiden sind der Classic-Zugang; s. `RELAY_ZUGANG`.
   */
  geraetekonto?: { login: string; password: string };
}

/**
 * Das Passwort dieser Sitzung, wenn der Schlüsselbund es nicht annimmt.
 *
 * Das ist kein Komfort, sondern der Umgang mit einer echten Lage: auf einem **unsignierten**
 * Bau (Simulator mit `CODE_SIGNING_ALLOWED=NO`) fehlt die Schlüsselbund-Berechtigung, und
 * jedes Schreiben scheitert mit `-34018` (`errSecMissingEntitlement`). Am 2026-09-11 am
 * Simulator gemessen. Dann gilt das Passwort **für diese Sitzung** — und die Oberfläche sagt
 * das, statt „gesichert" zu melden und beim nächsten Start mit leerem Feld dazustehen.
 */
let fluechtigesPasswort = '';

/**
 * Was in der **iOS-Einstellungen-App** steht — dort gehören die Zugangsdaten hin
 * (Nutzerentscheidung 2026-09-11): sie sind Sache der Einrichtung, und während einer Prüfung
 * sperrt der geführte Zugriff den Weg dorthin.
 *
 * `available: false` heisst „diese Plattform hat keine" (Browser). Dann bleiben die Felder in
 * der App-Oberfläche die Quelle.
 */
export async function verwalteteEinstellungen(): Promise<ManagedSettings> {
  try {
    return await Signing.getManagedSettings();
  } catch {
    return {
      url: '',
      login: '',
      password: '',
      modus: '',
      available: false,
    };
  }
}

/**
 * Der geltende Zugang. Reihenfolge, und zwar in dieser:
 *
 * 1. **iOS-Einstellungen** — sie gehören der Einrichtung und stechen alles andere.
 * 2. Was in der App eingetragen wurde (Entwicklung im Browser).
 * 3. Die Vorbelegung `VORGABE`.
 *
 * Ein **leeres** Feld in den iOS-Einstellungen zählt nicht als Angabe: iOS legt die
 * `DefaultValue` aus der `Root.plist` erst an, wenn jemand die Seite dort einmal geöffnet hat.
 * Ohne diese Regel stünde ein frisches iPad ohne Adresse da, obwohl in der `Root.plist` eine
 * steht.
 */
/**
 * Merkt sich das Gleis für den **Dateinamen** und gibt den Zugang unverändert zurück.
 *
 * ⚠️ **Jeder Weg zum Modus geht hier durch.** Die Ablage schreibt Relay' Begriffe
 * (`examItemContainerID`/`examItemID`) oder Classics (`stationID`/`taskID`), je nach Gleis
 * (`platform/storage.ts::setzeNamensform`). Stünde der Aufruf statt dessen in der Oberfläche,
 * fehlte er beim Start — und das erste Foto des Tages trüge die Begriffe des falschen Servers.
 */
function mitGleis(zugang: Zugangsdaten): Zugangsdaten {
  setzeNamensform(zugang.modus);
  return zugang;
}

export async function ladeZugang(store: Store, clientId: string): Promise<Zugangsdaten> {
  const settings = await loadSettings(store, clientId);
  // Einmal gemischt, für Serverzugang **und** Gerätekonto.
  const gemischt = leseQrUeberschreibungen(store, clientId)
    .catch(() => ({}))
    .then((qr) => leseEinstellungen(ausRelay(settings), qr));
  const geraetekonto = await gemischt
    .then((g) => ({ login: g.communication_login.wert, password: g.communication_password.wert }))
    .catch(() => ({ ...RELAY_ZUGANG }));
  const verwaltet = await verwalteteEinstellungen();
  let password = '';
  try {
    const gelesen = await Signing.getSecret({ key: PASSWORT_SCHLUESSEL });
    password = gelesen.value ?? '';
  } catch {
    // Kein Schlüsselbund (Browser vor dem ersten Setzen) — dann eben kein Passwort.
  }
  if (password === '') password = fluechtigesPasswort;

  if (verwaltet.available) {
    // ⚠️ **Die Geräteverwaltung steht über allem** (seit 2026-09-13, s.
    // `platform/einstellungen.ts`). Vorher las dieser Zweig nur das `Settings.bundle` — ein
    // MDM-Profil, das die Adresse vorgab, wirkte also auf jede Einstellung **ausser** auf die,
    // um die es dabei am ehesten geht.
    // ⚠️ **`qr` zählt hier mit**, sonst greift ein gescannter Code auf jede Einstellung ausser
    // auf die, um die es beim Scannen am ehesten geht: den Serverzugang. Die Rangfolge steht in
    // `platform/einstellungen.ts` — der Code schlägt das MDM, das MDM den Rest.
    const obenAuf = (quelle: string) => quelle === 'qr' || quelle === 'mdm';
    const vomMdm = await gemischt
      .then((g) => ({
        url: obenAuf(g.server_url.quelle) ? g.server_url.wert : '',
        login: obenAuf(g.classic_login.quelle) ? g.classic_login.wert : '',
        password: obenAuf(g.classic_password.quelle) ? g.classic_password.wert : '',
        modus: obenAuf(g.server_protocol.quelle) ? g.server_protocol.wert : '',
      }))
      .catch(() => ({ url: '', login: '', password: '', modus: '' }));

    return mitGleis({
      url: vomMdm.url || verwaltet.url || settings[SCHLUESSEL.url] || VORGABE.url,
      login: vomMdm.login || verwaltet.login || settings[SCHLUESSEL.login] || VORGABE.login,
      password: vomMdm.password || verwaltet.password || password || VORGABE.password,
      // Auch hier zählt ein leeres Feld nicht als Angabe: `alsModus` setzt dafür die
      // Katalogvorgabe ein — dieselbe, die der Einstellungsdialog anzeigt.
      modus: alsModus(vomMdm.modus || verwaltet.modus || settings[SCHLUESSEL.modus]),
      geraetekonto,
    });
  }
  const entwicklung = entwicklungsZugang();
  return mitGleis({
    url: (settings[SCHLUESSEL.url] ?? '') || entwicklung.url,
    login: (settings[SCHLUESSEL.login] ?? '') || entwicklung.login,
    password,
    modus: alsModus(settings[SCHLUESSEL.modus]),
    geraetekonto,
  });
}

/**
 * Zugang sichern. `passwortDauerhaft: false` heisst: das Passwort liegt nur für diese Sitzung
 * vor — der Schlüsselbund hat es abgelehnt (s. `fluechtigesPasswort`).
 */
export async function speichereZugang(
  store: Store,
  clientId: string,
  zugang: Zugangsdaten,
): Promise<{ passwortDauerhaft: boolean }> {
  await saveSetting(store, clientId, SCHLUESSEL.url, zugang.url.trim());
  await saveSetting(store, clientId, SCHLUESSEL.login, zugang.login.trim());
  await saveSetting(store, clientId, SCHLUESSEL.modus, zugang.modus);
  // Ab sofort schreibt die Ablage die Begriffe des gewählten Gleises — ohne Umweg über einen
  // Neustart, sonst trügen die Aufnahmen bis dahin die des vorherigen.
  setzeNamensform(zugang.modus);

  if (zugang.password === '') {
    fluechtigesPasswort = '';
    await Signing.deleteSecret({ key: PASSWORT_SCHLUESSEL }).catch(() => ({ ok: false }));
    return { passwortDauerhaft: true };
  }

  try {
    await Signing.setSecret({ key: PASSWORT_SCHLUESSEL, value: zugang.password });
    fluechtigesPasswort = '';
    return { passwortDauerhaft: true };
  } catch {
    // Nicht schlucken und nicht abbrechen: der Prüfer soll arbeiten können, aber wissen,
    // dass er das Passwort nach einem Neustart erneut eingibt.
    fluechtigesPasswort = zugang.password;
    return { passwortDauerhaft: false };
  }
}

/** Ob überhaupt ein Server eingerichtet ist. Adresse und Name genügen dafür. */
export function istEingerichtet(zugang: Pick<Zugangsdaten, 'url' | 'login'>): boolean {
  return zugang.url.trim() !== '' && zugang.login.trim() !== '';
}

export interface GeraetFuerAnmeldung {
  deviceIdentifier: string;
  deviceName: string;
}

/**
 * `Client.clientType` für die Anmeldung — Relay übernimmt ihn unverändert (relay-da,
 * 2026-11-01). Bis dahin stand hier fest `TABLET`, auch für Browser und Desktop.
 *
 * ⚠️ **Nicht `TABLET_OSCE`.** Relay führt zwei getrennte Wertelisten: `Client.clientType`
 * (`UNKNOWN`/`TABLET`/`BROWSER`/`DESKTOP`) und `ClientSetting.clientType` (`TABLET_OSCE`,
 * s. `persistence/settings.ts`). Die Anmeldung legt eine `Client`-Zeile an, also gilt die erste.
 */
export function relayGeraeteArt(): 'TABLET' | 'DESKTOP' | 'BROWSER' {
  return istGeraet() ? 'TABLET' : istDesktop() ? 'DESKTOP' : 'BROWSER';
}

/** Der Client für diesen Zugang. */
export function baueClient(zugang: Zugangsdaten, geraet: GeraetFuerAnmeldung): ServerClient {
  if (zugang.modus === 'classic') {
    // ⚠️ Der Classic-Server kennt **kein** Gerät: kein `deviceIdentifier`, kein `clientType`,
    // keine Anmeldung. Er sieht nur HTTP Basic. Die Geräteangaben gehen erst mit `POST
    // /rest/log` hinaus — das ist Etappe 2, nicht diese hier.
    return createClassicClient({
      zugang: { baseUrl: zugang.url, login: zugang.login, password: zugang.password },
    });
  }
  // Das Gerätekonto, nicht der Classic-Zugang — s. `RELAY_ZUGANG`. Ein von Hand gebauter Zugang
  // (Einstellungsdialog, Probe) trägt keins und nimmt die Vorgabe.
  const konto = zugang.geraetekonto ?? RELAY_ZUGANG;
  return createServerClient({
    zugang: { baseUrl: zugang.url, login: konto.login, password: konto.password },
    geraet: {
      deviceIdentifier: geraet.deviceIdentifier,
      deviceName: geraet.deviceName,
      clientType: relayGeraeteArt(),
      appType: 'OSCE',
      platformName: systemJetzt().name,
      appVersion: APP_VERSION_MIT_BUILD,
      osVersion: systemJetzt().version,
    },
  });
}

/**
 * Die Beispielprüfungen einlesen — der Demo-Weg ohne Server.
 *
 * Es ist **derselbe** Weg, den der Download später nimmt (`toBundle` → `importExam`), damit
 * die Demo nichts anderes übt als den Ernstfall. Wiederholbar: `importExam` ersetzt anhand
 * der Kennung, ein zweiter Aufruf legt nichts doppelt an.
 */
export async function ladeBeispielpruefungen(store: Store): Promise<number> {
  for (const beispiel of EXAMS) {
    await importExam(store, toBundle(beispiel));
  }
  return EXAMS.length;
}

/**
 * Der Client für den eingerichteten Server — oder `null`, wenn keiner eingerichtet ist.
 *
 * Bewusst `null` statt einer Ausnahme: „kein Server" ist derzeit der **Normalfall**, kein
 * Fehler. Wer ihn braucht, prüft auf `null` und sagt dem Prüfer etwas Nützliches.
 */
/**
 * Der Prüfungskatalog des eingerichteten Servers.
 *
 * Die **zweite und letzte** Stelle, an der der Modus etwas ändert. Beide Wege geben `ExamRef[]`
 * zurück; `mergeCatalogue` und der Prüfungsdialog darüber sehen keinen Unterschied.
 */
export async function holeKatalogFuer(
  modus: Servermodus,
  client: ServerClient,
  alleAnzeigen = false,
): Promise<ExamRef[]> {
  // ⚠️ `alleAnzeigen` wirkt **nur** im Classic-Modus: dort filtert der Server bei `current=true`
  // auf ein Zeitfenster um heute (`endDate >= heute` und `startDate <= heute + 14 Tage`) und
  // liefert sonst nur einen Bruchteil des Bestands. Relay' Geräteübersicht (`/exam/available`)
  // ist **ausdrücklich ungefiltert** — Startfenster und Ablauf wertet die App aus, Festlegung
  // des Nutzers vom 2026-09-12. Der Haken hätte dort nichts umzuschalten und wird in der
  // Oberfläche verborgen.
  return modus === 'classic' ? holeClassicKatalog(client, !alleAnzeigen) : holeKatalog(client);
}

export async function aktuellerClient(
  store: Store,
  clientId: string,
  geraet: Omit<GeraetFuerAnmeldung, 'deviceIdentifier'> & { deviceIdentifier: string },
): Promise<ServerClient | null> {
  const zugang = await ladeZugang(store, clientId);
  if (!istEingerichtet(zugang)) return null;
  return baueClient(zugang, geraet);
}
